/**
 * Auth Cache Utilities
 * 
 * Handles clearing user-specific React Query caches on auth state changes
 * to prevent stale data from previous sessions being used.
 */

import { QueryClient } from '@tanstack/react-query';
import { profileKeys } from '@/hooks/profile/queryKeys';

/**
 * Clears all user-specific query caches.
 * Should be called on logout and when a different user signs in.
 * 
 * This prevents the following issues:
 * - Stale profile data from previous user causing RLS violations
 * - Notification preferences showing wrong user's settings
 * - Other user-specific data persisting across sessions
 */
export function clearUserSpecificCaches(queryClient: QueryClient): void {
  // Clear profile cache - this is critical to prevent wrong profile_id being used
  queryClient.removeQueries({ queryKey: profileKeys.all });
  
  // Clear notification-related caches that depend on profile_id
  queryClient.removeQueries({ queryKey: ['user-notification-preferences'] });
  queryClient.removeQueries({ queryKey: ['notification-mutes'] });
  queryClient.removeQueries({ queryKey: ['notifications'] });
  queryClient.removeQueries({ queryKey: ['unreadCount'] });
  
  // Clear other user-specific caches
  queryClient.removeQueries({ queryKey: ['my-time-entries'] });
  queryClient.removeQueries({ queryKey: ['user-permissions'] });
  
  console.log('[AuthCache] Cleared user-specific query caches');
}

/**
 * Checks if the user has ACTUALLY changed between sessions.
 * Returns true ONLY when switching from one authenticated user to a different one.
 * 
 * Returns false when:
 * - Initial mount (previousUserId is undefined/null) - signOut already cleared on logout
 * - Same user re-authenticating
 * - Logout (handled explicitly by signOut function)
 */
export function hasUserChanged(previousUserId: string | null | undefined, newUserId: string | null | undefined): boolean {
  // Only clear cache when switching from one user to a DIFFERENT user
  // Both must be truthy (actual user IDs) and different
  if (previousUserId && newUserId && previousUserId !== newUserId) {
    return true;
  }
  
  return false;
}
